<template>
  <form action="#" class="mb-40" @submit.prevent="addFirstResult">
    <div class="row gx-xxl-6">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("adjustment_page.reference_no") }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="w-100 h-55 border-0 rounded-1 fs-14 text-black bg-white px-3 shadow-sm"
            :placeholder="t('adjustment_page.reference_no')"
            :value="referenceNo"
            readonly
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("adjustment_page.warehouse") }} <span class="text-danger">*</span></label>
          <select 
            class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55"
            :value="warehouseId"
            :disabled="warehouseLocked || isLoading"
            @change="$emit('update:warehouseId', ($event.target as HTMLSelectElement).value ? Number(($event.target as HTMLSelectElement).value) : null)"
          >
            <option :value="null">{{ t("adjustment_page.select_warehouse") }}</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">
              {{ w.name }}
            </option>
          </select>
          <small v-if="warehouseLocked" class="text-muted d-block mt-2">
            {{ t("adjustment_page.warehouse_locked_from_profile") }}
          </small>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group">
          <label class="d-block fs-14 text-black mb-2">{{ t("adjustment_page.adjustment_date") }}</label>
          <input
            type="date"
            class="w-100 h-55 border-0 rounded-1 fs-14 text-black bg-white px-3 shadow-sm"
            :value="adjustmentDate"
            readonly
          />
        </div>
      </div>
      <div class="col-12">
        <div class="form-group position-relative">
          <label class="d-block fs-14 text-black mb-2">{{ t("adjustment_page.choose_product") }}</label>
          <div class="adjustment-search-box position-relative w-100">
            <input
              ref="searchInput"
              type="text"
              :placeholder="t('adjustment_page.scan_search_product')"
              class="adjustment-search-input"
              v-model="searchQuery"
              @input="$emit('search-product', searchQuery)"
              @focus="isFocused = true"
              @keyup.enter.prevent="addFirstResult"
              autocomplete="off"
            />
            <span class="search-icon" aria-hidden="true">
              <i class="ri-search-line"></i>
            </span>
          </div>

          <div v-if="shouldShowResults" class="search-results-dropdown mt-2">
            <div class="result-list-header">
              <span>{{ normalizedQuery.length ? t("adjustment_page.matching_products") : t("adjustment_page.available_products") }}</span>
              <small>{{ t("adjustment_page.items_count", { count: searchResults.length }) }}</small>
            </div>

            <button
              v-for="product in searchResults"
              :key="product.id"
              type="button"
              class="result-item"
              @click="selectProduct(product)"
            >
              <div class="result-main">
                <strong class="result-name">{{ product.name }}</strong>
                <small class="result-meta">
                  {{ product.category?.name || t("adjustment_page.no_category") }}
                  <span class="meta-dot">•</span>
                  {{ warehouseName }}
                </small>
              </div>
              <div class="result-side">
                <span class="result-code">{{ product.code }}</span>
                <span class="result-stock" :class="{ 'is-low': getAvailableStock(product) <= 0 }">
                  {{ t("adjustment_page.stock") }}: {{ getAvailableStock(product) }}
                </span>
              </div>
            </button>

            <div v-if="searchResults.length === 0" class="result-empty-state">
              {{ t("adjustment_page.no_products_for_selected_warehouse") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Warehouse } from "../../../services/warehouse";
import { Product } from "../../../services/product";

export default defineComponent({
  name: "ChooseForm",
  props: {
    warehouses: {
      type: Array as () => Warehouse[],
      default: () => []
    },
    warehouseId: {
      type: [Number, String],
      default: null
    },
    warehouseLocked: {
      type: Boolean,
      default: false
    },
    adjustmentDate: {
      type: String,
      default: ""
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    referenceNo: {
      type: String,
      default: ""
    },
    searchResults: {
      type: Array as () => Product[],
      default: () => []
    },
    showResults: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:warehouseId', 'update:referenceNo', 'update:adjustmentDate', 'search-product', 'add-product'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const searchQuery = ref("");
    const searchInput = ref<HTMLInputElement | null>(null);
    const isFocused = ref(false);
    const normalizedQuery = computed(() => searchQuery.value.replace(/\s+/g, " ").trim());
    const shouldShowResults = computed(() => isFocused.value && props.showResults);
    const warehouseName = computed(() => {
      const selectedWarehouse = props.warehouses.find((warehouse) => Number(warehouse.id) === Number(props.warehouseId));
      return selectedWarehouse?.name || t("adjustment_page.selected_warehouse");
    });

    const getAvailableStock = (product: Product & { availableStock?: number }) =>
      Number(product.availableStock || 0);

    const selectProduct = (product: Product) => {
      emit('add-product', product);
      searchQuery.value = "";
      isFocused.value = false;
      searchInput.value?.focus();
    };

    const addFirstResult = () => {
      if (props.searchResults.length > 0) {
        selectProduct(props.searchResults[0]);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (searchInput.value?.contains(target)) return;
      const searchContainer = searchInput.value?.closest(".adjustment-search-box");
      if (searchContainer?.contains(target)) return;
      isFocused.value = false;
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      searchQuery,
      searchInput,
      isFocused,
      normalizedQuery,
      shouldShowResults,
      warehouseName,
      getAvailableStock,
      selectProduct,
      addFirstResult,
      t,
    };
  }
});
</script>

<style scoped>
.search-results-dropdown {
  position: absolute;
  width: 100%;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(20, 32, 62, 0.12);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
}

.adjustment-search-box {
  position: relative;
}

.adjustment-search-input {
  width: 100%;
  height: 55px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fff;
  color: #1f2940;
  font-size: 14px;
  padding: 0 52px 0 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.adjustment-search-input:focus {
  outline: none;
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.result-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  background: #f8fbff;
  font-size: 13px;
  color: #58627a;
}

.result-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 0;
  background: #fff;
  padding: 14px 16px;
  text-align: left;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f4f8;
}

.result-item:hover {
  background: #f8fbff;
}

.result-main,
.result-side {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.result-name {
  color: #1f2940;
  font-size: 15px;
}

.result-meta {
  color: #7f8aa3;
  font-size: 12px;
  margin-top: 2px;
}

.meta-dot {
  margin: 0 6px;
}

.result-code {
  align-self: flex-end;
  background: #edf4ff;
  color: #3563e9;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.result-stock {
  margin-top: 8px;
  align-self: flex-end;
  color: #1f9254;
  font-size: 12px;
  font-weight: 600;
}

.result-stock.is-low {
  color: #e5484d;
}

.result-empty-state {
  padding: 18px 16px;
  text-align: center;
  color: #7f8aa3;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #7f8aa3;
  font-size: 18px;
}
</style>
